import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, IconButton, Stack, Chip,
    CircularProgress, TextField,
    TablePagination, MenuItem, InputAdornment, useMediaQuery, useTheme,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import api from '../services/api';
import { toastSuccess, confirmAcao } from '../services/alerts';

const STATUS_OPCOES = [
    { v: 'ATIVO', l: 'Ativo' },
    { v: 'INATIVO', l: 'Inativo' },
    { v: 'ENCERRADO', l: 'Encerrado' },
];

const corStatus = (s) => {
    if (s === 'ATIVO') return 'success';
    if (s === 'ENCERRADO') return 'warning';
    return 'default';
};

export default function Prontuario() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [lista, setLista] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [busca, setBusca] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('');
    const [togglingId, setTogglingId] = useState(null);

    const debounceRef = useRef(null);
    const mountedRef = useRef(false);

    const carregar = async (pg, rpp, b, st) => {
        setLoading(true);
        try {
            const params = { page: pg, size: rpp };
            if (b) params.busca = b;
            if (st) params.status = st;
            const { data } = await api.get('/prontuarios', { params });
            setLista(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        carregar(0, rowsPerPage, '', '');
    }, []);

    useEffect(() => {
        if (!mountedRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            carregar(0, rowsPerPage, busca, statusFiltro);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [busca, statusFiltro]);

    useEffect(() => {
        if (!mountedRef.current) return;
        carregar(page, rowsPerPage, busca, statusFiltro);
    }, [page, rowsPerPage]);

    const formatarData = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '-';

    const toggleEncerrar = async (p) => {
        const encerrando = p.status !== 'ENCERRADO';
        const ok = await confirmAcao({
            title: encerrando ? 'Encerrar prontuário?' : 'Reabrir prontuário?',
            text: encerrando
                ? 'O prontuário ficará marcado como ENCERRADO. Você poderá reabri-lo depois.'
                : 'O prontuário voltará ao status ATIVO.',
            confirmButtonText: encerrando ? 'Encerrar' : 'Reabrir',
            cancelButtonText: 'Cancelar',
            icon: encerrando ? 'warning' : 'question',
        });
        if (!ok) return;
        setTogglingId(p.id);
        try {
            const novoStatus = encerrando ? 'ENCERRADO' : 'ATIVO';
            await api.patch(`/prontuarios/${p.id}/status`, { status: novoStatus });
            toastSuccess(encerrando ? 'Prontuário encerrado.' : 'Prontuário reaberto.');
            carregar(page, rowsPerPage, busca, statusFiltro);
        } catch (err) {
            console.error(err);
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2, mb: 3,
            }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Prontuários
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Total: {totalElements} {totalElements === 1 ? 'prontuário' : 'prontuários'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/prontuarios/novo')}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 3 }}>
                    Novo Prontuário
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField
                        fullWidth size="small"
                        placeholder="Buscar por paciente, número de registro ou CID..."
                        value={busca} onChange={(e) => setBusca(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: busca && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusca('')}><ClearIcon fontSize="small" /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField select size="small" label="Status" sx={{ minWidth: 180 }}
                        value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
                        <MenuItem value="">Todos</MenuItem>
                        {STATUS_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                    </TextField>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1100 }}>
                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: 110 }}>Número</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Paciente</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '18%' }}>Psicólogo</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 110 }}>Abertura</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 110 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '18%' }}>CID</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 100 }} align="center">Anamnese</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 100 }} align="center">Evoluções</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 140 }} align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: '#1ABB9C' }} />
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.length === 0 && (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: '#888' }}>
                                    Nenhum prontuário encontrado.
                                </TableCell></TableRow>
                            )}
                            {!loading && lista.map(p => {
                                const encerrado = p.status === 'ENCERRADO';
                                return (
                                    <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.numeroRegistro || '-'}</TableCell>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nomePaciente || ''}>{p.nomePaciente || '-'}</TableCell>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nomePsicologo || ''}>{p.nomePsicologo || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatarData(p.dataAbertura)}</TableCell>
                                        <TableCell><Chip size="small" label={p.status || '-'} color={corStatus(p.status)} /></TableCell>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.cid ? `${p.cid.codigo} - ${p.cid.descricao}` : ''}>{p.cid ? `${p.cid.codigo} - ${p.cid.descricao}` : '-'}</TableCell>
                                        <TableCell align="center">
                                            <Chip size="small" label={p.possuiAnamnese ? 'Sim' : 'Não'} color={p.possuiAnamnese ? 'success' : 'default'} />
                                        </TableCell>
                                        <TableCell align="center">{p.evolucoes?.length ?? 0}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Visualizar">
                                                <IconButton size="small" onClick={() => navigate(`/prontuarios/${p.id}?view=1`)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => navigate(`/prontuarios/${p.id}`)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={encerrado ? 'Reabrir prontuário' : 'Encerrar prontuário'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color={encerrado ? 'success' : 'warning'}
                                                        disabled={togglingId === p.id}
                                                        onClick={() => toggleEncerrar(p)}
                                                    >
                                                        {togglingId === p.id
                                                            ? <CircularProgress size={16} />
                                                            : (encerrado ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />)}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Linhas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>
        </Box>
    );
}
